import { parseChapterId } from '@skjv/common';
import type { BibleLocation } from '@skjv/common';

export interface ChapterObserverState {
  isInitializing: boolean;
  scrollAdjustmentUntil: number;
  visibleChapters: Map<string, number>;
  loadedChapters: Map<string, HTMLElement>;
  currentChapterId: string;
  onChapterChange?: (location: BibleLocation) => void;
}

/** Create an IntersectionObserver that tracks which chapter is most visible */
export function createChapterObserver(
  container: HTMLElement,
  state: ChapterObserverState,
  scheduleUpdate: () => void,
): IntersectionObserver {
  return new IntersectionObserver(
    (entries) => {
      // Skip during initialization or scroll adjustments
      if (state.isInitializing || Date.now() < state.scrollAdjustmentUntil) return;

      for (const entry of entries) {
        const chapterId = entry.target.id.replace('chapter-', '');
        if (entry.isIntersecting) {
          state.visibleChapters.set(chapterId, entry.intersectionRatio);
        } else {
          state.visibleChapters.delete(chapterId);
        }
      }

      scheduleUpdate();
    },
    {
      root: container,
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
    },
  );
}

/** Determine the most visible chapter and fire onChapterChange if it changed */
export function updateCurrentChapterFromObserver(
  container: HTMLElement,
  state: ChapterObserverState,
): void {
  if (state.visibleChapters.size === 0) return;

  // Find the chapter with the highest visibility ratio
  let bestChapterId = '';
  let bestRatio = -1;

  for (const [chapterId, ratio] of state.visibleChapters) {
    if (ratio > bestRatio) {
      bestRatio = ratio;
      bestChapterId = chapterId;
    }
  }

  // If there's a tie or multiple chapters with high visibility,
  // prefer the one that's more towards the top of the viewport
  if (bestRatio < 0.5 && state.visibleChapters.size > 1) {
    // Fall back to position-based detection for edge cases
    const chapters = Array.from(state.visibleChapters.keys());
    const containerRect = container.getBoundingClientRect();
    const viewportMiddle = containerRect.height / 2;

    for (const chapterId of chapters) {
      const element = state.loadedChapters.get(chapterId);
      if (element) {
        const rect = element.getBoundingClientRect();
        const relativeTop = rect.top - containerRect.top;
        const relativeBottom = rect.bottom - containerRect.top;

        // Check if this chapter spans the middle of the viewport
        if (relativeTop <= viewportMiddle && relativeBottom >= viewportMiddle) {
          bestChapterId = chapterId;
          break;
        }
      }
    }
  }

  if (bestChapterId && bestChapterId !== state.currentChapterId) {
    state.currentChapterId = bestChapterId;
    const location = parseChapterId(bestChapterId);
    if (location && state.onChapterChange) {
      state.onChapterChange(location);
    }
  }
}
